import { useEffect, useState } from 'react';
import { usePlayerStore } from '@/store/usePlayerStore';
import { useSettingsStore } from '@/store/useSettingsStore';

const VOLUME_STEP = 0.05;
const SEEK_STEP = 10;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts() {
  const [showHelp, setShowHelp] = useState(false);
  const setVolume = useSettingsStore((s) => s.setVolume);
  const toggleMute = useSettingsStore((s) => s.toggleMute);
  const triggerReload = usePlayerStore((s) => s.triggerReload);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const player = document.querySelector('video');
      if (!player) return;

      switch (e.key) {
        case ' ':
        case 'k':
        case 'K': {
          e.preventDefault();
          if (player.paused) void player.play();
          else player.pause();
          break;
        }
        case 'm':
        case 'M': {
          e.preventDefault();
          player.muted = !player.muted;
          toggleMute();
          break;
        }
        case 'f':
        case 'F': {
          e.preventDefault();
          if (document.fullscreenElement) {
            void document.exitFullscreen();
          } else {
            // Fullscreen the video's own container (the aspect-video
            // wrapper), not its grandparent. `data-player-container` is
            // set on the containerRef div in VideoPlayer; querying by
            // attribute is robust to DOM refactors and works for both
            // keyboards triggered from the player area and from
            // anywhere else on the page.
            const container = player.closest<HTMLElement>('[data-player-container]');
            void container?.requestFullscreen();
          }
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const next = Math.min(1, player.volume + VOLUME_STEP);
          player.volume = next;
          if (player.muted) player.muted = false;
          setVolume(next);
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const next = Math.max(0, player.volume - VOLUME_STEP);
          player.volume = next;
          setVolume(next);
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          player.currentTime = Math.max(0, player.currentTime - SEEK_STEP);
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          const dur = Number.isFinite(player.duration) ? player.duration : player.currentTime + SEEK_STEP;
          player.currentTime = Math.min(dur, player.currentTime + SEEK_STEP);
          break;
        }
        case '?': {
          e.preventDefault();
          setShowHelp((s) => !s);
          break;
        }
        case 'r':
        case 'R': {
          if (e.shiftKey) {
            e.preventDefault();
            triggerReload();
          }
          break;
        }
        case 'Escape': {
          if (showHelp) {
            e.preventDefault();
            setShowHelp(false);
          }
          break;
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setVolume, showHelp, toggleMute, triggerReload]);

  return { showHelp, setShowHelp };
}
